import { useEffect, useState } from 'react';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import EventDetailSheet from '../components/EventDetailSheet';
import useAutoRefresh from '../utils/refresh';

export default function BookShift() {
  const { user, refreshUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [selections, setSelections] = useState({}); // {event_role_id: 'apply'}
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  // Refetch when the app returns to the foreground, when BottomNav's poll
  // sees a counter rise, or when the active tab is tapped. See utils/refresh.js.
  const refreshKey = useAutoRefresh();
  // ── Session AJ: event details sheet ──────────────────────────────────────
  // `description` and `transport` already arrive here — buildEvents spreads
  // `...e` — but this screen never showed either. An agent could write "you
  // must have your own transport, there is no shuttle" and nobody deciding
  // whether to apply would ever read it.
  //
  // ⚠️ INFORMATION ONLY. Apply is NOT in here, and that is not a preference:
  // applying on this screen is a SELECTION that feeds the fixed "Submit N
  // Applications" bar at the bottom of the viewport. A sheet covering that bar
  // would hide the only confirmation that a selection registered.
  //
  // ⚠️ Triggered by a dedicated Details BUTTON, not by tapping the card — a
  // Book a Shift card holds one Apply button per role plus Select-all-days, so
  // a whole-card tap would need a stopPropagation on every one of them and
  // would risk swallowing the primary action. Schedule.js can use a card tap
  // because its cards have two buttons; this one cannot.
  const [detailEvent, setDetailEvent] = useState(null);

  useEffect(() => {
    // Refresh the cached profile before rendering: the skills banner, the
    // pending lock and the per-staff rate check all read `user`, which would
    // otherwise still hold whatever was true at login. /api/events already
    // reads skills fresh from the DB, so without this the two disagree —
    // matching shifts appear while the banner claims no skills are assigned.
    refreshUser();
    axios.get('/api/events').then(r => setEvents(r.data)).finally(() => setLoading(false));
    // ⚠️ `selections` is deliberately NOT cleared on a refresh. Someone who
    // ticks two roles, switches to WhatsApp and comes back keeps their ticks.
    // If a role filled up meanwhile, bookSelected already skips any selection
    // whose role no longer exists in `events`.
  }, [refreshUser, refreshKey]);

  const toggleSelect = (eventRoleId, type) => {
    setSelections(s => {
      const cur = s[eventRoleId];
      if (cur === type) { const n={...s}; delete n[eventRoleId]; return n; }
      return {...s, [eventRoleId]: type};
    });
  };

  const bookSelected = async () => {
    const entries = Object.entries(selections);
    if (entries.length === 0) return;
    setBooking(true); setError(''); setSuccess('');
    let ok = 0;
    for (const [eventRoleId, type] of entries) {
      // find event_id for this role
      let eventId = null;
      for (const e of events) {
        const r = e.roles?.find(r => String(r.id) === String(eventRoleId));
        if (r) { eventId = e.id; break; }
      }
      if (!eventId) continue;
      try {
        await axios.post(`/api/events/${eventId}/apply`, { event_role_id: parseInt(eventRoleId), is_standby: 0 });
        ok++;
      } catch(e) {
        if (!e.response?.data?.error?.includes('Already applied')) setError('Some could not be submitted.');
      }
    }
    if (ok > 0) setSuccess(`${ok} application${ok > 1 ? 's' : ''} submitted! Check your Schedule.`);
    setSelections({});
    const r = await axios.get('/api/events');
    setEvents(r.data);
    setBooking(false);
  };

  // Session AK: opening is now just state — the backdrop, the close paths and
  // the Android-back handling all live inside EventDetailSheet, so Book a Shift
  // and both agent previews behave identically without three copies of it.
  const openDetail = (ev) => setDetailEvent(ev);

  const isPending = user?.status === 'pending';
  const hasSkills = (user?.skills || '').split(',').map(s => s.trim()).filter(Boolean).length > 0;
  const totalSelected = Object.keys(selections).length;


  const now = new Date();
  // Absolute [start, end) window for a shift, overnight-aware (end rolls to next day)
  const shiftWindow = (s) => {
    const startT = s.time_start || '00:00';
    const endT   = s.time_end   || '23:59';
    const start = new Date(`${s.date}T${startT}`);
    const end   = new Date(`${s.date}T${endT}`);
    const [sh, sm] = startT.split(':').map(Number);
    const [eh, em] = endT.split(':').map(Number);
    if ((eh * 60 + em) < (sh * 60 + sm)) end.setDate(end.getDate() + 1);
    return { start, end };
  };

  const confirmedOn = (e) => e.roles?.some(r => r.my_application === 'accepted' && r.my_is_standby !== 1);

  // Build absolute time windows for confirmed shifts. Session M1: carry the
  // event id so a series day the staff member is CONFIRMED on (which stays
  // visible inside its series card, unlike a standalone confirmed event) never
  // counts as clashing with itself.
  const confirmedWindows = events
    .filter(e => confirmedOn(e))
    .map(e => ({ id: e.id, ...shiftWindow(e) }));

  const overlapsConfirmed = (e) => {
    const w = shiftWindow(e);
    return confirmedWindows.some(c => c.id !== e.id && c.start < w.end && w.start < c.end);
  };

  const isFuture = (e) => {
    try {
      const start = e.time_start || '00:00';
      const endT = e.time_end || '23:59';
      const end = new Date(`${e.date}T${endT}`);
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = endT.split(':').map(Number);
      if ((eh * 60 + em) < (sh * 60 + sm)) end.setDate(end.getDate() + 1); // overnight — ends next day
      if (end < now) return false;
    } catch {}
    return true;
  };

  const isSeries = (e) => (e.series_size || 1) > 1;

  // Session M1: how one DAY of a series presents to this staff member.
  // 'confirmed' → shown with its confirmed state (series context matters, so
  // it is not hidden the way a standalone confirmed event is); 'clash' → shown
  // disabled with an explanation; 'open' → normal role rows. The apply-time
  // server guards (accept Guard 3 + silent-reject) stay the real enforcement.
  const dayState = (e) => {
    if (confirmedOn(e)) return 'confirmed';
    if (overlapsConfirmed(e)) return 'clash';
    return 'open';
  };

  // Visibility. Standalone events: unchanged rules (hide past, hide already-
  // confirmed, hide clashing). Series days: hide past days only — confirmed
  // and clashing days stay visible inside the series card so "Day 3 of 5"
  // always reads truthfully.
  const futureEvents = events.filter(e => {
    if (!isFuture(e)) return false;
    if (isSeries(e)) return true;
    if (confirmedOn(e)) return false;
    if (overlapsConfirmed(e)) return false;
    return true;
  });

  const hasOpen = e => e.roles?.some(r => !r.my_application && r.slots_filled < r.spots_available);
  const byStart = (a, b) => shiftWindow(a).start - shiftWindow(b).start;

  // Session M1: group series days under one card. A series where every visible
  // day is already confirmed is dropped — those shifts live in Schedule, same
  // as a confirmed standalone event.
  const groups = (() => {
    const out = []; const byKey = new Map();
    for (const e of [...futureEvents].sort(byStart)) {
      if (isSeries(e)) {
        const k = e.parent_event_id || e.id;
        if (!byKey.has(k)) { const g = { type: 'series', key: k, days: [] }; byKey.set(k, g); out.push(g); }
        byKey.get(k).days.push(e);
      } else out.push({ type: 'single', e });
    }
    return out.filter(g => g.type === 'single' || !g.days.every(d => dayState(d) === 'confirmed'));
  })();

  const groupStart = g => g.type === 'single' ? shiftWindow(g.e).start : shiftWindow(g.days[0]).start;
  const groupOpen = g => g.type === 'single'
    ? hasOpen(g.e)
    : g.days.some(d => dayState(d) === 'open' && hasOpen(d));
  // Sort: groups with open spots chronologically first, then the rest chronologically
  const sortedGroups = [
    ...groups.filter(groupOpen).sort((a, b) => groupStart(a) - groupStart(b)),
    ...groups.filter(g => !groupOpen(g)).sort((a, b) => groupStart(a) - groupStart(b)),
  ];

  const myRateSet = user?.personal_rate != null && user.personal_rate !== '';

  // ── One role row (Session M1: extracted so the standalone card and every
  //    day of a series card render applications identically) ──────────────────
  const RoleRow = ({ role, locked = false, seriesDay = false }) => {
    const sel = selections[role.id];
    const applied = role.my_application;
    const isStandby = role.my_is_standby === 1 && applied;
    const full = role.slots_filled >= role.spots_available;
    // Session M1 tweak: on a series day, the flat "per event" rate is earned
    // PER DAY (each day is its own event) — say so. Standalone events keep
    // the stored wording. True whole-series flat rates are the M3 decision.
    const payLabel = (seriesDay && role.pay_unit === 'per_event')
      ? String(role.pay_rate || '').replace('for the event', 'for the day')
      : role.pay_rate;
    // Per-staff pay: rate comes from the staff member's own profile. If this role is
    // per-staff and they have no rate set, they can't be paid for it — block applying.
    const perStaff = role.pay_unit === 'per_staff';
    const myRate = myRateSet ? Number(user.personal_rate) : null;
    const rateMissing = perStaff && myRate == null;

    return (
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{role.role}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>{role.slots_filled}/{role.spots_available} filled</span>
          </div>
          {!applied && (
            <span className="badge badge-open" style={{ fontSize: 9 }}>
              {full ? 'FULL' : `${role.spots_available - role.slots_filled} OPEN`}
            </span>
          )}
          {applied === 'accepted' && !isStandby && <span className="badge badge-confirmed" style={{ fontSize: 9 }}>CONFIRMED</span>}
          {applied === 'pending' && !isStandby && <span className="badge badge-pending" style={{ fontSize: 9 }}>PENDING</span>}
          {isStandby && <span className="badge badge-agency" style={{ fontSize: 9 }}>ON STANDBY</span>}
        </div>

        {perStaff ? (
          <div style={{ marginBottom: 8 }}>
            {myRate != null ? (
              <span className="pay-chip">💰 Your rate: R{myRate}/hr</span>
            ) : (
              <span className="pay-chip" style={{ background: 'rgba(234,179,8,0.12)', color: '#a16207', border: '1px solid rgba(234,179,8,0.4)' }}>⚠️ No pay rate set</span>
            )}
          </div>
        ) : role.pay_rate && (
          <div style={{ marginBottom: 8 }}>
            <span className="pay-chip">💰 {payLabel}</span>
          </div>
        )}
        {!applied && !full && !locked && (
          rateMissing ? (
            <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.35)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Your agency hasn't set your pay rate for per-staff shifts yet. Ask them to set it so you can apply.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => toggleSelect(role.id, 'apply')}
                style={{ flex: 1, padding: '9px', borderRadius: 6, border: `2px solid ${sel === 'apply' ? '#16a34a' : '#16a34a'}`, background: sel === 'apply' ? '#16a34a' : 'rgba(22,163,74,0.08)', color: sel === 'apply' ? '#fff' : '#16a34a', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}
              >
                {sel === 'apply' ? '✓ Selected' : 'Apply'}
              </button>
            </div>
          )
        )}
        {!applied && full && (
          <div style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
            This role is fully staffed
          </div>
        )}
      </div>
    );
  };

  const EventCard = ({ e }) => (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 16px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border-light)' }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', letterSpacing: '-0.3px', marginBottom: 5 }}>{e.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <span>📅 {formatDate(e.date)}</span>
          <span>🕐 {e.time_start}–{e.time_end}</span>
          <span>📍 {[e.location, e.street_address].filter(Boolean).join(', ')}</span>
        </div>
        {e.dress_code && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>👔 {e.dress_code}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {/* Session AJ. Always shown — an event with no description or
              transport still has its address, times and roles worth reading in
              one place, and a button that appears and disappears between cards
              is harder to learn than one that is always there. */}
          <button onClick={() => openDetail(e)} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>ⓘ Details</button>
          {e.brief_url && (
            <button onClick={() => window.open(e.brief_url, '_blank', 'noopener,noreferrer')} style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.07)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>📄 Read Brief</button>
          )}
        </div>
      </div>

      {/* One row per role */}
      {e.roles?.map(role => <RoleRow key={role.id} role={role} />)}
    </div>
  );

  // ── Session M1: multi-day series card — one card, per-day apply ─────────────
  const applyableRoles = d => (d.roles || []).filter(r =>
    !r.my_application && r.slots_filled < r.spots_available && !(r.pay_unit === 'per_staff' && !myRateSet));
  const daySelected = d => (d.roles || []).some(r => selections[r.id]);

  // "Select all days": one tap selects one open role on every applyable day —
  // preferring the same role name across days (your selection elsewhere in the
  // series wins, else the first day's first open role). Days already selected,
  // confirmed, clashing or full are left untouched. Tapping again clears the
  // series' selections. Each selection still submits as ONE application per
  // day, exactly as if tapped individually.
  const toggleAllDays = (days) => {
    const selectable = days.filter(d => dayState(d) === 'open' && applyableRoles(d).length > 0);
    if (selectable.length === 0) return;
    const allSelected = selectable.every(daySelected);
    if (allSelected) {
      setSelections(s => {
        const n = { ...s };
        for (const d of days) for (const r of (d.roles || [])) delete n[r.id];
        return n;
      });
      return;
    }
    let preferred = null;
    for (const d of days) { const sel = (d.roles || []).find(r => selections[r.id]); if (sel) { preferred = sel.role; break; } }
    if (!preferred) preferred = applyableRoles(selectable[0])[0]?.role || null;
    setSelections(s => {
      const n = { ...s };
      for (const d of selectable) {
        if ((d.roles || []).some(r => n[r.id])) continue; // day already has a selection
        const opts = applyableRoles(d);
        const pick = (preferred && opts.find(r => r.role === preferred)) || opts[0];
        if (pick) n[pick.id] = 'apply';
      }
      return n;
    });
  };

  const SeriesCard = ({ days }) => {
    const first = days[0];
    const last = days[days.length - 1];
    const brief = days.find(d => d.brief_url)?.brief_url || null;
    const selectable = days.filter(d => dayState(d) === 'open' && applyableRoles(d).length > 0);
    const allSelected = selectable.length > 0 && selectable.every(daySelected);
    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: '3px solid var(--blue,#2563eb)', borderRadius: 12, padding: '18px 16px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-primary)', letterSpacing: '-0.3px', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {first.title}
            <span className="badge badge-series" style={{ fontSize: 9 }}>MULTI-DAY</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <span>📅 {formatDate(first.date)} – {formatDate(last.date)}</span>
            <span>📍 {[first.location, first.street_address].filter(Boolean).join(', ')}</span>
          </div>
          {first.dress_code && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>👔 {first.dress_code}</div>}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Apply per day — each day is its own shift with its own check-in and pay.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {/* Session AJ. `first` carries description/transport: the create
                route copies both to every day of a series, so day 1 is
                representative. The date RANGE is passed separately so the sheet
                can show the whole series rather than day 1 alone. */}
            <button onClick={() => openDetail({ ...first, __seriesLast: last, __seriesDays: days.length })} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>ⓘ Details</button>
            {brief && (
              <button onClick={() => window.open(brief, '_blank', 'noopener,noreferrer')} style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.07)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>📄 Read Brief</button>
            )}
            {selectable.length > 1 && (
              <button onClick={() => toggleAllDays(days)} style={{ fontSize: 12, fontWeight: 700, color: allSelected ? 'var(--text-secondary)' : '#16a34a', background: allSelected ? 'var(--bg-surface)' : 'rgba(22,163,74,0.08)', border: `1px solid ${allSelected ? 'var(--border)' : 'rgba(22,163,74,0.35)'}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>
                {allSelected ? 'Clear all days' : `Select all ${selectable.length} days`}
              </button>
            )}
          </div>
        </div>

        {days.map(d => {
          const st = dayState(d);
          return (
            <div key={d.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                  <strong>Day {d.day_number}</strong>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>{formatDate(d.date)} · {d.time_start}–{d.time_end}</span>
                </div>
                {st === 'confirmed' && <span className="badge badge-confirmed" style={{ fontSize: 9 }}>CONFIRMED</span>}
                {st === 'clash' && <span className="badge badge-rejected" style={{ fontSize: 9 }}>UNAVAILABLE</span>}
              </div>
              {st === 'clash' ? (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
                  This day clashes with another confirmed shift on your schedule.
                </div>
              ) : st === 'confirmed' ? (
                (d.roles || []).filter(r => r.my_application).map(role => <RoleRow key={role.id} role={role} locked seriesDay />)
              ) : (
                d.roles?.map(role => <RoleRow key={role.id} role={role} seriesDay />)
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="app-shell">
      <div className="screen">
        <div className="topbar">
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>Book a Shift</h2>
        </div>

        {isPending ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>Shifts locked</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Your account must be approved before you can browse and apply for shifts.</p>
          </div>
        ) : loading ? (
          <div className="loading">Loading shifts...</div>
        ) : (
          <>
            {!hasSkills && (
              <div style={{ margin: '12px 16px', padding: 14, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.4)', borderRadius: 8, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                ⚠️ <strong>No skills assigned yet.</strong> Your coordinator needs to assign your skills before you can apply for shifts. Contact them to get set up.
              </div>
            )}
            {success && <div style={{ margin: '12px 16px', padding: 14, background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: 8, fontSize: 13, color: 'var(--green)' }}>✓ {success}</div>}
            {error && <div style={{ margin: '12px 16px', padding: 14, background: 'rgba(var(--danger-rgb),0.08)', border: '1px solid var(--danger)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>{error}</div>}

            {sortedGroups.length > 0 && (
              <div style={{ padding: '0 16px' }}>
                {sortedGroups.map(item => item.type === 'single'
                  ? <EventCard key={item.e.id} e={item.e} />
                  : <SeriesCard key={`series-${item.key}`} days={item.days} />)}
              </div>
            )}
            {sortedGroups.length === 0 && <div className="empty-state">No upcoming shifts available right now.<br />Check back soon.</div>}

            <div style={{ padding: '4px 16px 8px' }}>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>*Select shifts to apply. Confirmed shifts appear in your Schedule.*</p>
            </div>
          </>
        )}
      </div>

      {/* Session AK: the sheet body now lives in EventDetailSheet, shared with
          both agent previews. ⚠️ Do NOT re-inline it — a second copy is how
          `transport` stayed invisible for months. */}
      <EventDetailSheet event={detailEvent} onClose={() => setDetailEvent(null)} />

      {!isPending && totalSelected > 0 && (
        <div style={{ position: 'fixed', bottom: 'calc(64px + env(safe-area-inset-bottom))', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 428, padding: '0 16px', zIndex: 15 }}>
          <button className="btn btn-primary" onClick={bookSelected} disabled={booking}>
            {booking ? 'Submitting...' : `Submit ${totalSelected} Application${totalSelected > 1 ? 's' : ''}`}
          </button>
        </div>
      )}
      <BottomNav />
    </div>
  );
}
